import React from 'react';
import { CheckCircle, Clock, Lightning } from '@phosphor-icons/react';

/**
 * OperationalMetrics — lightweight inline metrics for agent cards.
 * Shows: last run time, runs today, success rate indicator.
 *
 * Props:
 *  - lastRunAt: string | null (ISO timestamp)
 *  - runsToday: number
 *  - successRate: number (0-100, optional)
 *  - className: string
 */

function formatRelativeTime(timestamp) {
  if (!timestamp) return 'Never';
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${days}d ago`;
}

export const OperationalMetrics = ({
  lastRunAt = null,
  runsToday = 0,
  successRate = null,
  className = '',
}) => {
  const hasRun = !!lastRunAt;

  return (
    <div
      className={`flex items-center gap-1 text-[10px] text-[var(--text-muted)] ${className}`}
      data-testid="operational-metrics"
    >
      {hasRun ? (
        <>
          <Clock size={10} weight="duotone" className="shrink-0" />
          <span>Last run: {formatRelativeTime(lastRunAt)}</span>
          <span className="text-[#3a3f52] mx-0.5">&bull;</span>
          <Lightning size={10} weight="duotone" className="shrink-0" />
          <span>Runs today: {runsToday}</span>
          {successRate !== null && successRate >= 0 && (
            <>
              <span className="text-[#3a3f52] mx-0.5">&bull;</span>
              <span className={`inline-flex items-center gap-0.5 ${
                successRate >= 90 ? 'text-emerald-400' : successRate >= 70 ? 'text-amber-400' : 'text-red-400'
              }`}>
                <CheckCircle size={10} weight="duotone" />
                {successRate}%
              </span>
            </>
          )}
        </>
      ) : (
        <span className="text-[#4a4f62] italic">Ready to run</span>
      )}
    </div>
  );
};

export default OperationalMetrics;
